/**
 * Admin → Users: the typed seam between the screen and `src/server/admin_users.py`.
 *
 * One module, like `brandingApi.ts`, so the screen never hand-rolls a fetch and every call
 * inherits `lib/auth.ts`'s single token source (the httponly session cookie) and its one 401
 * path. Nothing here stores a credential, and the one place a plaintext secret ever appears —
 * `reset_url` — is returned to the caller and deliberately never persisted: see
 * `UsersScreen`'s handling of it.
 *
 * `last_seen_at` is `string | null` and stays that way all the way to the cell. Null means
 * "has never signed in", which is a FACT, not missing data, and the screen renders it with its
 * reason rather than as an empty cell. Coercing it to "" or to `created_at` here would destroy
 * the distinction before the UI ever saw it.
 */
import { apiFetch } from "./auth";

export interface PlatformUser {
  id: string;
  name: string;
  email: string;
  role: string;
  is_admin: boolean;
  status: "active" | "disabled";
  org_id: string | null;
  org_name: string | null;
  created_at: string | null;
  /** Null means never signed in. Never coerce this. */
  last_seen_at: string | null;
  /** False means the account was created without a password and is waiting on a reset link. */
  has_password: boolean;
  /** §6 — the monthly report limit (`server/report_limit.py` is the API contract). `null` on a
   * non-admin means "the default of 10", never coerced to the number 10 here: the screen is the
   * one place that prints "(default)" versus a set figure, and it needs to know which of the two
   * this is. Always `null` on an admin, who is never limited (see `monthly_limit_*` below, which
   * stay `null` for an admin too). */
  monthly_report_limit: number | null;
  /** The acting admin's user id — not for display; see `monthly_limit_set_by_name`. */
  monthly_limit_set_by: string | null;
  /** The name to SHOW. Falls back to the id above only if a server has not been upgraded to send
   * this field yet. */
  monthly_limit_set_by_name?: string | null;
  monthly_limit_set_at: string | null;
  monthly_limit_reason: string | null;
  /** `resolve_monthly_limit`'s own answer — null for an admin, else the figure actually in force
   * (the override, the group limit, or the default). Not relied on by `MonthlyLimitCell`, which
   * derives the same fact from `is_admin` / `monthly_report_limit` directly, but carried through
   * for a caller that wants the resolved number without re-deriving it. */
  effective_monthly_limit?: number | null;
}

export interface OrgSummary {
  id: string;
  name: string;
  members: number;
}

export interface CreateUserBody {
  name: string;
  email: string;
  role?: string;
  /** Omit entirely to create the account with no password and get a reset link back. */
  password?: string;
  is_admin?: boolean;
  org_id?: string;
  org_name?: string;
}

export interface CreatedUser {
  user: PlatformUser;
  /** Present only when no password was chosen. Shown once and then dropped. */
  reset_url: string | null;
  expires_at: string | null;
}

export interface ResetLink {
  url: string;
  expires_at: string;
}

export interface UserList {
  users: PlatformUser[];
  /**
   * Whether this deployment can send email at all. The screen's standing notice reads off this
   * rather than off a sentence somebody wrote once: today it is false everywhere, and when SMTP
   * lands the notice changes wording instead of disappearing.
   */
  mail_configured: boolean;
}

export const listUsers = () => apiFetch<UserList>("/api/admin/users");

export const listOrgs = () =>
  apiFetch<{ orgs: OrgSummary[] }>("/api/admin/orgs").then((r) => r.orgs);

export const createUser = (body: CreateUserBody) =>
  apiFetch<CreatedUser>("/api/admin/users", { method: "POST", body });

export const createResetLink = (id: string) =>
  apiFetch<ResetLink>(`/api/admin/users/${encodeURIComponent(id)}/reset-link`, { method: "POST" });

export const setUserStatus = (id: string, status: "active" | "disabled") =>
  apiFetch<{ user: PlatformUser }>(`/api/admin/users/${encodeURIComponent(id)}/status`, {
    method: "POST",
    body: { status },
  });

export const setUserAdmin = (id: string, is_admin: boolean) =>
  apiFetch<{ user: PlatformUser }>(`/api/admin/users/${encodeURIComponent(id)}/admin`, {
    method: "POST",
    body: { is_admin },
  });

export const setUserOrg = (id: string, org: { org_id?: string; org_name?: string }) =>
  apiFetch<{ user: PlatformUser }>(`/api/admin/users/${encodeURIComponent(id)}/org`, {
    method: "POST",
    body: org,
  });

export const setUserPassword = (id: string, password: string) =>
  apiFetch<{ ok: boolean; sessions_revoked: number }>(
    `/api/admin/users/${encodeURIComponent(id)}/password`,
    { method: "POST", body: { password } },
  );

/** Redeem a reset token. PUBLIC — this is the one call on an unauthenticated screen. */
export const redeemReset = (token: string, password: string) =>
  apiFetch<{ ok: boolean; sessions_revoked: number }>("/api/auth/reset", {
    method: "POST",
    body: { token, password },
    onUnauthorized: () => {
      /* No redirect: nobody is signed in here, and that is the normal case. */
    },
  });

/**
 * A date for a human, or null passed straight through.
 *
 * The caller decides what null LOOKS like; this refuses to decide for them. Every date the
 * API returns is an ISO string, and an unparseable one renders as itself rather than as
 * "Invalid Date" — a value we cannot read is still a value somebody can copy into a bug report.
 */
export function formatDay(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
